// The vote for the next game, as a list: a row per game with its icon, the
// name, and how many of the phones here picked it. The row this llama
// picked wears its colour. A press picks, a press on the picked row takes
// the pick back. When every phone agrees the game sends the countdown, and
// the ask row counts it down on the room's clock. No scoreboard: the night's
// board is on the screen, and a phone shows only the action of the moment.
//
// The first phone in holds the force (`host` in the data). Under the list it
// gets one more row: start the game it picked when the room has not agreed,
// or hand the pick back to the room once it has forced one.

import type { Activity } from '../../protocol';
import { buzz, el, image } from '../dom';
import { type ListRow, list } from '../list';
import type { Page } from '../page';
import { say } from '../say';
import { play } from '../sfx';
import { roomTime } from '../state';
import { STRINGS } from '../strings';

interface VoteGame {
  id: string;
  name: string;
  // The bare name of the game's icon, when the game sends one.
  icon?: string;
}

// The vote's data as the game sends it (`party_vote.gd`, `data`).
interface VoteData {
  games: VoteGame[];
  picks: Record<string, string>;
  need: number;
  // The seat that holds the force, or -1 with nobody here.
  host: number;
  // The game the host phone or the screen forced, or empty.
  forced: string;
  ends_at: number;
  settled: string;
}

const NO_HOST = -1;

const readVote = (activity: Activity): VoteData => {
  const data = (activity.data ?? {}) as Partial<VoteData>;
  return {
    games: Array.isArray(data.games) ? data.games : [],
    picks: data.picks ?? {},
    need: data.need ?? 0,
    host: data.host ?? NO_HOST,
    forced: data.forced ?? '',
    ends_at: data.ends_at ?? 0,
    settled: data.settled ?? '',
  };
};

// The host phone's row under the list, or null for every other phone and
// for a host that has picked nothing yet.
function hostRow(page: Page, activity: Activity, vote: VoteData, mine: string): ListRow | null {
  const state = page.state();
  if (state.seat === null || state.seat !== vote.host) return null;
  const forced = vote.games.find((game) => game.id === vote.forced);
  if (forced) {
    return {
      name: STRINGS.vote.release,
      onPress: () => {
        buzz();
        play('click');
        page.send({ t: 'play', id: activity.id, data: { force: null } });
      },
    };
  }
  const picked = vote.games.find((game) => game.id === mine);
  if (!picked) return null;
  return {
    name: STRINGS.vote.start(picked.name),
    onPress: () => {
      buzz();
      play('click');
      page.send({ t: 'play', id: activity.id, data: { force: picked.id } });
    },
  };
}

export function buildVote(page: Page, activity: Activity): HTMLElement {
  const state = page.state();
  const vote = readVote(activity);
  const body = el('div', 'body body-vote');
  if (activity.phase === 'go') {
    body.append(say(STRINGS.vote.go));
    return body;
  }
  const ask = el('div', 'ask');
  const settled = vote.games.find((game) => game.id === vote.settled);
  if (activity.phase === 'countdown' && settled) {
    ask.append(say(STRINGS.vote.starting(settled.name), 'label'), el('p', 'vote-countdown'));
  } else {
    ask.append(say(STRINGS.vote.ask, 'label'));
  }
  body.append(ask);
  const mine = state.seat === null ? '' : (vote.picks[String(state.seat)] ?? '');
  const counts = new Map<string, number>();
  for (const id of Object.values(vote.picks)) counts.set(id, (counts.get(id) ?? 0) + 1);
  const rows: ListRow[] = vote.games.map((game) => ({
    lead: game.icon ? image('item-icon', `/game/${game.icon}.png`, '') : undefined,
    name: game.name,
    value: vote.need > 0 ? STRINGS.vote.picked(counts.get(game.id) ?? 0, vote.need) : '',
    picked: game.id === mine,
    onPress: () => {
      buzz();
      play('click');
      const pick = game.id === mine ? null : game.id;
      page.send({ t: 'play', id: activity.id, data: { pick } });
    },
  }));
  body.append(list(rows, 'vote-list'));
  const host = hostRow(page, activity, vote, mine);
  if (host) body.append(list([host], 'vote-host'));
  return body;
}

// The countdown, in whole seconds on the room's clock, redrawn in place.
export function liveVote(page: Page, root: HTMLElement, activity: Activity): void {
  const holder = root.querySelector<HTMLElement>('.vote-countdown');
  if (!holder) return;
  const vote = readVote(activity);
  const left = Math.max(1, Math.ceil((vote.ends_at - roomTime(page.state(), Date.now())) / 1000));
  const text = String(left);
  if (holder.textContent !== text) holder.textContent = text;
}
